"use client";

import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from "@heroui/react";
import { Download, ExternalLink, X } from "lucide-react";

interface PetraDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PetraDownloadModal = ({ isOpen, onClose }: PetraDownloadModalProps) => {
  const handleDownloadPetra = () => {
    // Detect if we're on mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    if (isMobile) {
      // Mobile: redirect to app store
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      
      if (isIOS) {
        window.open('https://apps.apple.com/app/petra-aptos-wallet/id1668969132', '_blank');
      } else if (isAndroid) {
        window.open('https://play.google.com/store/apps/details?id=com.petra.wallet', '_blank');
      } else {
        // Fallback to iOS App Store
        window.open('https://apps.apple.com/app/petra-aptos-wallet/id1668969132', '_blank');
      }
    } else {
      // Desktop: redirect to Chrome Web Store
      window.open('https://chromewebstore.google.com/detail/petra-aptos-wallet/ejjladinnckdgjemekebdpeokbikhfci?hl=en', '_blank');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      placement="center"
      scrollBehavior="inside"
      hideCloseButton
      classNames={{
        backdrop: "bg-black/50 backdrop-blur-sm",
        wrapper: "z-50 flex items-center justify-center",
        base: "bg-[#0C1113] text-white mx-4 my-4 max-h-[90vh] rounded-xl",
        body: "p-0",
        header: "hidden",
      }}
    >
      <ModalContent>
        {() => (
          <>
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-50 w-8 h-8 flex items-center justify-center text-white hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header with Download Icon */}
            <div className="flex items-center gap-3 p-6 pb-4">
              <div className="p-2 bg-[#60FF8A]/10 rounded-lg">
                <Download className="w-5 h-5 text-[#60FF8A]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Petra Wallet Required</h2>
                <p className="text-sm text-gray-400">Connect to Aptos blockchain</p>
              </div>
            </div>
            
            <ModalBody className="p-0">
              <div className="p-6">
                <div className="space-y-4">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-r from-[#60FF8A]/20 to-emerald-500/20 rounded-2xl flex items-center justify-center">
                <img 
                  src="https://pulsarmoney.b-cdn.net/rhuna/icon3.png" 
                  alt="Petra Wallet" 
                  className="w-12 h-12"
                />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">
                Install Petra Wallet
              </h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                To use Rhuna Portal, you need to install Petra Wallet - the official Aptos blockchain wallet. 
                {typeof window !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
                  ? " Download the mobile app from your app store, then return to this page to connect." 
                  : " Install the browser extension from the Chrome Web Store."}
              </p>
            </div>

            <div className="bg-[#1F2937] rounded-lg p-4 space-y-3">
              <h5 className="text-white font-semibold text-sm">Why Petra Wallet?</h5>
              <div className="space-y-2 text-sm text-gray-300">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#60FF8A] rounded-full"></div>
                  <span>Sub-second transactions with ultra-low fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#60FF8A] rounded-full"></div>
                  <span>Built by Aptos Labs - the official wallet</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#60FF8A] rounded-full"></div>
                  <span>Your keys, your crypto - fully self-custodial</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#60FF8A] rounded-full"></div>
                  <span>Independently audited for security</span>
                </div>
              </div>
            </div>

            <div className="text-center text-xs text-gray-500">
              {typeof window !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
                ? "After installing the app, return to this page and click Connect Wallet again"
                : "After installing, refresh this page to connect your wallet"}
            </div>
                </div>
              </div>
            </ModalBody>
            
            {/* Footer Buttons */}
            <div className="p-6 pt-0">
              <div className="flex gap-3 justify-center">
                <Button
                  variant="light"
                  onPress={onClose}
                  className="text-gray-400 hover:text-white"
                >
                  Maybe Later
                </Button>
                <Button
                  onPress={handleDownloadPetra}
                  className="bg-[#60FF8A] text-black font-semibold hover:bg-[#60FF8A]/90"
                >
                  Install Petra Wallet
                </Button>
              </div>
            </div>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
